import { ChainProxy } from './chains'
import { ContactCompanyType } from './contact_companies'
import { Deal } from './deals'
import { Tag } from './tags'

// ChainCompany is still a chain on the backend
export interface ChainCompany {
  chain: number
  name: string
  domain?: string
  count?: number
}

export interface ContactCompany {
  id: number
  chain?: ChainProxy
  name: string
  domain?: string
  category?: string
  sub_category?: SubCategory
  notes?: string
  taglist?: Tag[] | null | undefined
  size?: number
  deals?: Deal[]
  distributors?: CompanyDistributor[]
  gpos?: CompanyGPO[]
  note_count: number
  deal_count: number
  contact_count: number
  email_count?: number
  document_count?: number
  university_id?: number
  k12district_id?: number
  hospital_id?: number
  nursing_home_id?: number
  company_type?: ContactCompanyType
  days_since_last_modified?: number
  lodging_id?: number
}

export interface CompanyEmailExtension {
  id: number
  email_extension: string
  created: Date
  modified: Date
}

export interface CompanyDetailsData {
  id: number
  name: string
  email_extensions: Array<CompanyEmailExtension>
  logos: Array<CompanyLogo>
  billing_email: string | null
  stripe_customer_id: string | null
  rebate_support_email: string | null
  sample_customer_service_email: string | null
  contract_start_date: string | null
  annual_contact_credits: number | null
  activate_type: 'none' | 'full' | 'limited'
  seat_buckets: Array<{
    user_types: string[]
    active: number
    limit: number
  }>
  contact_request_credits?: {
    total: number
    available: number
    used: number
    pending: number
  }
  signal_filter_level?: string
  llm_context?: string
  enabled_signal_types?: string[]
}

export interface CompanyLogo {
  id: number
  company: number
  logo?: string
  logo_url?: string
  original_filename?: string
  is_primary: boolean
  created: Date
  modified: Date
}

export interface CompanyEmailExtensionRequests {
  id: number
  email_extension: string
  note?: string
  admin_reason?: string
  status: CompanyEmailExtensionRequestsStatus
  created: Date
  modified: Date
}

export enum CompanyEmailExtensionRequestsStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  DENIED = 'denied',
  CANCELED = 'canceled',
}

export const CompanyEmailExtensionRequestsStatusMap = {
  pending: 'Pending',
  approved: 'Approved',
  denied: 'Denied',
  canceled: 'Canceled',
}

export interface ContactCompanySearchResult {
  contact_company_id?: number // Made optional since chain results may not have this
  item_id?: string | number // Chain ID from chain search results
  name: string
  sub_category_name?: string
  domain: string
  category?: number // Made optional for chain results
  sub_category?: number // Made optional for chain results
  size?: number
  count?: number // Number of locations for chains
  score?: number
  category_name?: string
  city?: string
  state?: string
  address_line1?: string
  zipcode?: string
  country?: string
  created_date?: string
  type?: string // 2-letter code for First Bite results (RE, NH, CU, etc.)
  sub_category_slug?: string // Slug for unmatched results
  city_summary?: string // City summary for chain results
  state_summary?: string // State summary for chain results (e.g., "CA, CO, FL, UT, +5 more")
}

export interface DoorSearchResult {
  id: number
  item_id: string
  name: string
  domain?: string
  address?: string
  city?: string
  state?: string
  zipcode?: string
  chain_id?: number
  chain_proxy_id?: number
  door_proxy_id?: number
  chain_name?: string
  chain_size?: number
  type?: string
  phone?: string
  latitude?: number
  longitude?: number
  score?: number
  category_name?: string
  sub_category_name?: string
}

export interface ContactCompanyCreateRequest {
  id?: number
  chain?: number
  name?: string
  domain?: string
  category?: number
  sub_category?: number
}

export type CompanyCategorySlug =
  | 'non-commercial'
  | 'commercial'
  | 'retail'
  | 'intermediaries'
  | 'manufacturer'
  | 'other'

// COMPANY_TYPE_UPDATE
export type CompanyTypeSlugWithFBData =
  | 'restaurants-bars'
  | 'education-cu'
  | 'education-k-12'
  | 'healthcare-hospitals'
  | 'healthcare-nursinghomes'
  | 'lodging-hospitality'
  | 'convenience'

// This type is used to specify the company type (subcategory)
// It should never be extended out of the scope of the subcategories defined in the db.
export type CompanyTypeSlug =
  | CompanyTypeSlugWithFBData
  | 'commercial-other'
  | 'retail-other'
  | 'intermediaries-other'
  | 'manufacturer-other'
  | 'other-other'
  | 'business-industry'
  | 'healthcare-hospital-care'
  | 'other-fire-depts-prisons-military'
  | 'transportation-planes-trains-cruises'
  | 'leisure'
  | 'catering-party-service'
  | 'retail-outlets'
  | 'food-specialist-retailers'
  | 'grocery-stores'
  | 'brokers'
  | 'wholesalers-and-distributors'
  | 'franchisee'
  | 'group-procurement-organizations-gpos'
  | 'contract-management-companies'
  | 'management-firms'
  | 'consultants'
  | 'technology-providers'
  | 'food-manufacturer'
  | 'contract-manufacturers-cmo'
  | 'ingredient-manufacturer'

// This type is used to specify the table for the purposes of:
// - Routing API calls made to common action functions. E.g. commonManageTags
// - Getting the correct table name
export type TableTypeSlug =
  | CompanyTypeSlug
  | 'companies-table'
  | 'restaurants-bars-doors'
  | 'contacts-table'
  | 'contact-tags'
  | 'deals-table'
  | 'deals-table-reports'
  | 'lodging-hospitality-doors'
  | 'convenience-doors'
  | 'deals-pipeline-table'
  | 'campaigns-table'
  | 'activity-table'
  | 'activity-my-table'
  | 'rebate-claims-table'
  | 'rebate-offers-table'
  | 'unmatched-doors-table'
  | 'doors-reports-table'
  | 'chains-reports-table'
  | 'importer'

export type SubCategory = {
  id: number
  name: string
  slug: CompanyTypeSlug
  description: string
}

export type ContactType = SubCategory

export type Category = {
  id: number
  name: string
  slug: CompanyCategorySlug
  description: string
  sub_categories: SubCategory[]
}

export type CompanyDistributor = {
  id: number
  name: string
  description: string
  url: string
  is_default: boolean
}

export type CompanyDistributorCreate = {
  name: string
  description?: string
  url?: string
}

export type CompanyGPO = {
  id: number
  name: string
  description: string
  url: string
  is_default: boolean
}

export type CompanyGPOCreate = {
  name: string
  description?: string
  url?: string
}

// Company types that support contact requests
// COMPANY_TYPE_UPDATE
export const ALLOWED_CONTACT_REQUEST_TYPES: CompanyTypeSlugWithFBData[] = [
  'restaurants-bars',
  'education-cu',
  'education-k-12',
  'healthcare-hospitals',
  'healthcare-nursinghomes',
  'lodging-hospitality',
  'convenience',
]

export const COMPANY_CATEGORIES_AND_TYPES = {
  'Non-Commercial': [
    'Business & Industry',
    'Education',
    'Healthcare (Hospital, Care)',
    'Other (Fire Depts, Prisons, Military)',
  ],
  Commercial: [
    'Restaurants & Bars',
    'Lodging & Hospitality',
    'Transportation (Planes, Trains, Cruises)',
    'Leisure (Sport, Theme Parks, Museums)',
    'Catering, Party Service',
    'Other',
  ],
  Retail: [
    'Retail Outlets',
    'Food Specialist Retailers',
    'Grocery Stores',
    'Convenience Stores',
    'Other',
  ],
  Intermediaries: [
    'Brokers',
    'Wholesalers and Distributors',
    'Franchisee',
    'Group Procurement Organizations (GPOs)',
    'Contract Management Companies',
    'Management Firms',
    'Consultants',
    'Technology Providers',
  ],
  Manufacturers: [
    'Food Manufacturer',
    'Contract Manufacturers (CMO)',
    'Ingredient Manufacturer',
    'Other Manufacturer ',
  ],
}

export type CampaignCreate = {
  name: string
  type_slug?: string
  color?: string
  description?: string
}
